import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

// Image imports
import KFHD from "./Images/KFHD.jpeg";
import MPHM from "./Images/MPHM.jpeg";
import TheGovernorateOffice from "./Images/TheGovernorateOffice.jpeg";
import TGOQ from "./Images/TGOQ.jpeg";
import QU from "./Images/QU.jpeg";
import MOH from "./Images/MOH.jpeg";
import mapImage from "./Images/map.jpg";
import KSU from "./Images/KSU.jpeg";
import KAH from "./Images/KAH.JPG";
import GHR from "./Images/GHR.JPG";
import AQH from "./Images/AQH.JPEG";
import CSB from './Images/csb.jpg'
const ProjectSection = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState("");
  const [showAllProjects, setShowAllProjects] = useState(false);

  const scrollContainerRef = useRef(null);

  const isRTL = i18n.dir() === "rtl";
  const fontFamily = isRTL
    ? "'Tajawal', Arial, sans-serif"
    : "Arial, Helvetica, sans-serif";

  const handleOpenModal = (image, title) => {
    setSelectedImage(image);
    setSelectedTitle(title);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedImage(null);
    setSelectedTitle("");
  };

  const projects = [
    { title: t("pro1"), sector: t("prod1"), image: KSU },
    { title: t("pro2"), sector: t("prod2"), image: KFHD },
    { title: t("pro3"), sector: t("prod3"), image: MPHM },
    { title: t("pro4"), sector: t("prod4"), image: TheGovernorateOffice },
    { title: t("pro5"), sector: t("prod5"), image: TGOQ },
    { title: t("pro6"), sector: t("prod6"), image: QU },
    { title: t("pro7"), sector: t("prod7"), image: MOH },
    { title: t("pro8"), sector: t("prod8"), image: KAH },
    { title: t("pro9"), sector: t("prod9"), image: GHR },
    { title: t("pro10"), sector: t("prod10"), image: AQH },
    { title: t("pro11"), sector: t("prod11"), image: CSB },
  ];

  // Auto-scroll effect for right panel
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let scrollInterval;
    let isPaused = false;

    const scrollSpeed = 4;

    const startAutoScroll = () => {
      if (scrollInterval || isPaused) return;
      scrollInterval = setInterval(() => {
        if (!isPaused) {
          if (
            container.scrollTop + container.clientHeight >=
            container.scrollHeight
          ) {
            container.scrollTop = 0;
          } else {
            container.scrollTop += scrollSpeed;
          }
        }
      }, 20);
    };

    const stopAutoScroll = () => {
      clearInterval(scrollInterval);
      scrollInterval = null;
    };

    const pauseAutoScroll = () => {
      isPaused = true;
      stopAutoScroll();
    };

    const resumeAutoScroll = () => {
      isPaused = false;
      startAutoScroll();
    };

    startAutoScroll();

    container.addEventListener("mouseenter", pauseAutoScroll);
    container.addEventListener("mouseleave", resumeAutoScroll);
    container.addEventListener("touchstart", pauseAutoScroll);
    container.addEventListener("touchend", resumeAutoScroll);

    return () => {
      stopAutoScroll();
      container.removeEventListener("mouseenter", pauseAutoScroll);
      container.removeEventListener("mouseleave", resumeAutoScroll);
      container.removeEventListener("touchstart", pauseAutoScroll);
      container.removeEventListener("touchend", resumeAutoScroll);
    };
  }, []);

  return (
    <Box
      sx={{
        fontFamily,
        position: "relative",
        backgroundImage: `url(${mapImage})`,
        backgroundSize: "cover",
        borderRadius: "20px",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        color: "white",
        mt: { xs: 2, sm: 3, md: 0 },
        p: { xs: 2, sm: 3, md: 4 },
        height: { xs: "auto", md: "110vh" },
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.14)",
          borderRadius: "20px",
          zIndex: 1,
        },
        "& > *": {
          position: "relative",
          zIndex: 2,
        },
      }}
    >
      <Box>
        <Grid container spacing={4}>
          {/* Left Panel */}
          <Grid item xs={12} md={8}>
            <Box>
              <Typography
                variant="h4"
                fontFamily={fontFamily}
                fontWeight={600}
                gutterBottom
                sx={{ pt: "70px" }}
              >
                {t("ourProjectsTitle")}
              </Typography>
              <Typography variant="body1" fontFamily={fontFamily} sx={{ mb: 2 }}>
                {t("projectsDescription")}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowAllProjects(true)}
                sx={{ mb: 2, borderRadius: 3, fontFamily }}
              >
                {t("allProjectsButton") || "All Projects"}
              </Button>
            </Box>
          </Grid>

          {/* Right Panel (Auto-scroll) */}
          <Grid
            item
            xs={12}
            md={4}
            sx={{ marginTop: "30px", height: isSmallScreen ? "auto" : "1100px" }}
          >
            <Box
              ref={scrollContainerRef}
              sx={{
                height: "100%",
                overflowY: "auto",
                pr: 1,
                "&::-webkit-scrollbar": { display: "none" },
                pt: 3,
              }}
            >
              {projects.map((project, index) => (
                <Card
                  key={index}
                  sx={{
                    borderRadius: 4,
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                    border: "1px solid white",
                    backgroundImage: `url(${project.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    height: "300px",
                    marginBottom: 3,
                  }}
                  onClick={() => handleOpenModal(project.image, project.title)}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      zIndex: 1,
                    }}
                  />
                  <CardContent
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 2,
                      textAlign: "center",
                      color: "white",
                      width: { xs: "90%", sm: "80%", md: "60%" },
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontFamily={fontFamily}
                      gutterBottom
                      sx={{ textShadow: "2px 2px 5px rgba(0, 0, 0, 0.7)" }}
                    >
                      {project.title}
                    </Typography>
                    <Divider sx={{ my: 1, borderColor: "white" }} />
                    <Typography fontSize={11} fontFamily={fontFamily}>
                      {project.sector}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Single Project Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="md">
        <DialogContent sx={{ position: "relative", p: 3 }}>
          <IconButton
            onClick={handleCloseModal}
            sx={{ position: "absolute", top: 10, right: 10, zIndex: 2 }}
          >
            <CloseIcon sx={{ color: "black" }} />
          </IconButton>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <img
              src={selectedImage}
              alt={selectedTitle}
              style={{ width: "100%", height: "auto", borderRadius: "12px" }}
            />
          </Box>
          <Typography
            variant="h6"
            fontFamily={fontFamily}
            textAlign="center"
            sx={{ mt: 2 }}
          >
            {selectedTitle}
          </Typography>
        </DialogContent>
      </Dialog>

      {/* All Projects Modal */}
      <Dialog
        open={showAllProjects}
        onClose={() => setShowAllProjects(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogContent sx={{ position: "relative", p: 3 }}>
          <IconButton
            onClick={() => setShowAllProjects(false)}
            sx={{ position: "absolute", top: 10, right: 10, zIndex: 2 }}
          >
            <CloseIcon sx={{ color: "black" }} />
          </IconButton>

          <Typography
            variant="h5"
            fontFamily={fontFamily}
            textAlign="center"
            gutterBottom
          >
            {t("allProjectsTitle") || "All Projects"}
          </Typography>

          <Grid container spacing={3}>
            {projects.map((project, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    height: "300px",
                    cursor: "pointer",
                    position: "relative",
                    backgroundImage: `url(${project.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    border: "1px solid #ddd",
                  }}
                  onClick={() => {
                    setShowAllProjects(false);
                    handleOpenModal(project.image, project.title);
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      zIndex: 1,
                    }}
                  />
                  <CardContent
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 2,
                      textAlign: "center",
                      color: "white",
                      width: "90%",
                    }}
                  >
                    <Typography variant="h6" fontFamily={fontFamily} gutterBottom>
                      {project.title}
                    </Typography>
                    <Divider sx={{ my: 1, borderColor: "white" }} />
                    <Typography fontSize={11} fontFamily={fontFamily}>
                      {project.sector}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProjectSection;
